import React, {
  useEffect,
  useMemo,
  useState,
} from "react";

import {
  Card,
  Col,
  Form,
  Row,
  Spinner,
  Table,
  Button,
} from "react-bootstrap";

import {
  AlertCircle,
  CheckCircle,
  FileText,
  RefreshCcw,
  Search,
  Users,
} from "react-feather";

import {
  useDispatch,
  useSelector,
} from "react-redux";

import {
  useNavigate,
} from "react-router-dom";

import {
  fetchAccountDashboardThunk,
} from "../../../store/slices/accountDashboardSlice";

import "../../../css/master-common.css";

// ============================================================
// HELPERS
// ============================================================

const money = (value) =>
  new Intl.NumberFormat("en-IN", {
    style: "currency",
    currency: "INR",
    maximumFractionDigits: 2,
  }).format(Number(value || 0));

const labelize = (value) =>
  String(value || "")
    .replaceAll("_", " ")
    .replace(
      /\b\w/g,
      (c) => c.toUpperCase()
    );

const normalizePanIds = (value) => {
  if (!Array.isArray(value)) {
    return [];
  }

  return value
    .map((id) => Number(id))
    .filter(
      (id) =>
        Number.isFinite(id) &&
        id > 0
    );
};

// ============================================================
// PAGE
// ============================================================

const AccountDashboardPage = () => {
  const dispatch =
    useDispatch();

  const navigate =
    useNavigate();

  // ==========================================================
  // REDUX
  // ==========================================================

  const {
    cards = {},

    category_summary = [],

    panchayath_outstanding = [],

    recent_receipts = [],

    monthly_chart = [],

    loading = false,

    error = null,
  } = useSelector(
    (state) =>
      state.accountDashboard ||
      {}
  );

  // ==========================================================
  // TENANT
  // ==========================================================

  const tenantPanIds =
    useSelector(
      (state) =>
        state.tenant?.pan_ids ||
        []
    );

  const tenantPanName =
    useSelector(
      (state) =>
        state.tenant?.pan_name ||
        "Select Panchayath"
    );

  const panIds =
    useMemo(
      () =>
        normalizePanIds(
          tenantPanIds
        ),
      [tenantPanIds]
    );

  const panIdsKey =
    panIds.join(",");

  // ==========================================================
  // FILTERS
  // ==========================================================

  const [
    filters,
    setFilters,
  ] = useState({
    category: "",
    from_date: "",
    to_date: "",
  });

  // ==========================================================
  // LOAD
  // ==========================================================

  useEffect(() => {
    if (!panIds.length) {
      return;
    }

    dispatch(
      fetchAccountDashboardThunk({
        pan_ids: panIds,

        category:
          filters.category,

        from_date:
          filters.from_date,

        to_date:
          filters.to_date,
      })
    );

    // Reload when top TenantSwitcher changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    dispatch,
    panIdsKey,
  ]);

  // ==========================================================
  // SEARCH
  // ==========================================================

  const handleSearch = () => {
    if (!panIds.length) {
      return;
    }

    dispatch(
      fetchAccountDashboardThunk({
        pan_ids:
          panIds,

        ...filters,
      })
    );
  };

  // ==========================================================
  // RESET
  // ==========================================================

  const handleReset = () => {
    const next = {
      category: "",
      from_date: "",
      to_date: "",
    };

    setFilters(next);

    if (!panIds.length) {
      return;
    }

    dispatch(
      fetchAccountDashboardThunk({
        pan_ids:
          panIds,

        ...next,
      })
    );
  };

  // ==========================================================
  // REFRESH
  // ==========================================================

  const handleRefresh = () => {
    if (!panIds.length) {
      return;
    }

    dispatch(
      fetchAccountDashboardThunk({
        pan_ids:
          panIds,

        ...filters,
      })
    );
  };

  // ==========================================================
  // COLLECTION RATE
  // ==========================================================

  const collectionRate =
    useMemo(() => {
      const invoice =
        Number(
          cards.total_invoice_amount ||
            cards.total_bill_amount ||
            0
        );

      const received =
        Number(
          cards.total_received ||
            0
        );

      if (
        invoice <= 0
      ) {
        return 0;
      }

      return Math.min(
        100,
        (received / invoice) *
          100
      );
    }, [
      cards,
    ]);

  // ==========================================================
  // INVOICE PENDING REMINDER
  //
  // Condition:
  // Bill is generated but invoice is NOT created yet.
  //
  // This is NOT based on:
  // - outstanding balance
  // - partially paid
  // - unpaid invoice
  // ==========================================================

  const invoicePendingCount =
    Number(
      cards
        ?.invoice_pending_count ||
        0
    );

  const invoicePendingBillAmount =
    Number(
      cards
        ?.invoice_pending_bill_amount ||
        0
    );

  const hasInvoicePending =
    invoicePendingCount > 0;

  // ==========================================================
  // OPEN BILLS WAITING FOR INVOICE
  // ==========================================================

  const openInvoicePendingBills =
    () => {
      navigate(
        "/settings/panchayath-billing?category=subscription&invoice_status=not_created"
      );
    };

  // ==========================================================
  // UI
  // ==========================================================

  return (
    <div className="pb-page">
      {/* =====================================================
          HEADER
      ===================================================== */}

      <div className="pb-header">
        <div className="pb-header-left">
          <h3 className="pb-title">
            Account Dashboard
          </h3>

         

          <div className="text-muted small mt-1">
            Showing:{" "}
            {tenantPanName}
          </div>
        </div>

        <div className="pb-header-actions">
          <Button
            className="pb-btn pb-btn-outline"
            disabled={
              loading ||
              !panIds.length
            }
            onClick={
              handleRefresh
            }
          >
            <RefreshCcw
              size={16}
            />

            Refresh
          </Button>
        </div>
      </div>

      {/* =====================================================
          FILTERS
      ===================================================== */}

      <Card className="pb-filter-card">
        <Card.Body>
          <Row className="g-3 align-items-end">
            <Col
              xl={3}
              md={6}
            >
              <Form.Label>
                Category
              </Form.Label>

              <Form.Select
                className="pb-control"
                value={
                  filters.category
                }
                onChange={(e) =>
                  setFilters(
                    (prev) => ({
                      ...prev,

                      category:
                        e.target
                          .value,
                    })
                  )
                }
              >
                <option value="">
                  All Categories
                </option>

                <option value="subscription">
                  Subscription
                </option>

                <option value="installation">
                  Installation
                </option>

                <option value="training">
                  Training
                </option>

                <option value="printer">
                  Printer
                </option>

                <option value="other">
                  Other
                </option>
              </Form.Select>
            </Col>

            <Col
              xl={3}
              md={6}
            >
              <Form.Label>
                From Date
              </Form.Label>

              <Form.Control
                type="date"
                className="pb-control"
                value={
                  filters.from_date
                }
                onChange={(e) =>
                  setFilters(
                    (prev) => ({
                      ...prev,

                      from_date:
                        e.target
                          .value,
                    })
                  )
                }
              />
            </Col>

            <Col
              xl={3}
              md={6}
            >
              <Form.Label>
                To Date
              </Form.Label>

              <Form.Control
                type="date"
                className="pb-control"
                value={
                  filters.to_date
                }
                min={
                  filters.from_date ||
                  undefined
                }
                onChange={(e) =>
                  setFilters(
                    (prev) => ({
                      ...prev,

                      to_date:
                        e.target
                          .value,
                    })
                  )
                }
              />
            </Col>

            <Col
              xl="auto"
              md={6}
              className="pb-filter-actions"
            >
              <Button
                className="pb-btn pb-btn-primary"
                onClick={
                  handleSearch
                }
                disabled={
                  loading ||
                  !panIds.length
                }
              >
                <Search
                  size={15}
                />

                Apply
              </Button>

              <Button
                className="pb-btn pb-btn-light"
                onClick={
                  handleReset
                }
                disabled={
                  loading ||
                  !panIds.length
                }
              >
                <RefreshCcw
                  size={15}
                />

                Reset
              </Button>
            </Col>
          </Row>
        </Card.Body>
      </Card>

      {/* =====================================================
          NO TENANT
      ===================================================== */}

      {!panIds.length && (
        <div className="alert alert-warning">
          Select a Panchayath
          from the top Tenant
          Switcher.
        </div>
      )}

      {/* =====================================================
          ERROR
      ===================================================== */}

      {error && (
        <div className="alert alert-danger">
          {error}
        </div>
      )}

      {/* =====================================================
          LOADING
      ===================================================== */}

      {loading ? (
        <Card className="pb-table-card">
          <Card.Body className="text-center py-5">
            <Spinner />

            <div className="mt-3 text-muted">
              Loading account
              dashboard...
            </div>
          </Card.Body>
        </Card>
      ) : (
        <>
          {/* =================================================
              INVOICE PENDING REMINDER
          ================================================= */}

          {hasInvoicePending && (
            <div
              className="pb-subscription-notice"
              role="button"
              tabIndex={0}
              onClick={
                openInvoicePendingBills
              }
              onKeyDown={(e) => {
                if (
                  e.key === "Enter" ||
                  e.key === " "
                ) {
                  openInvoicePendingBills();
                }
              }}
            >
              <div className="pb-subscription-notice-icon">
                <FileText
                  size={21}
                />
              </div>

              <div className="pb-subscription-notice-content">
                <strong>
                  Invoice Creation Pending
                </strong>

                <span>
                  {
                    invoicePendingCount
                  }{" "}
                  subscription bill
                  {invoicePendingCount === 1
                    ? ""
                    : "s"}{" "}
                  {invoicePendingCount === 1
                    ? "is"
                    : "are"}{" "}
                  generated but the invoice has not been created yet.
                  {" "}
                  Total bill value:{" "}
                  <b>
                    {money(
                      invoicePendingBillAmount
                    )}
                  </b>
                  .
                </span>
              </div>

              <div className="pb-subscription-notice-action">
                Create Invoice →
              </div>
            </div>
          )}

          {/* =================================================
              PRIMARY CARDS
          ================================================= */}

          <Row className="g-3 pb-summary-row">
            <Col
              xl={3}
              md={6}
            >
              <div className="pb-summary-card">
                <div className="pb-summary-icon pb-icon-blue">
                  <Users
                    size={21}
                  />
                </div>

                <div className="pb-summary-content">
                  <span className="pb-summary-label">
                    Total Panchayaths
                  </span>

                  <strong className="pb-summary-value">
                    {cards.total_panchayaths ||
                      0}
                  </strong>

                  <small>
                    {cards.active_panchayaths ||
                      0}{" "}
                    active
                  </small>
                </div>
              </div>
            </Col>

            <Col
              xl={3}
              md={6}
            >
              <div className="pb-summary-card">
                <div className="pb-summary-icon pb-icon-purple">
                  <FileText
                    size={21}
                  />
                </div>

                <div className="pb-summary-content">
                  <span className="pb-summary-label">
                    Total Bills
                  </span>

                  <strong className="pb-summary-value">
                    {cards.total_bills ||
                      0}
                  </strong>

                  <small>
                    {cards.pending_bills ||
                      0}{" "}
                    pending
                  </small>
                </div>
              </div>
            </Col>

            <Col
              xl={3}
              md={6}
            >
              <div className="pb-summary-card">
                <div className="pb-summary-icon pb-icon-green">
                  <CheckCircle
                    size={21}
                  />
                </div>

                <div className="pb-summary-content">
                  <span className="pb-summary-label">
                    Amount Received
                  </span>

                  <strong className="pb-summary-value">
                    {money(
                      cards.total_received
                    )}
                  </strong>

                  <small>
                    {collectionRate.toFixed(
                      1
                    )}
                    % collection
                  </small>
                </div>
              </div>
            </Col>

            <Col
              xl={3}
              md={6}
            >
              <div className="pb-summary-card">
                <div className="pb-summary-icon pb-icon-orange">
                  <AlertCircle
                    size={21}
                  />
                </div>

                <div className="pb-summary-content">
                  <span className="pb-summary-label">
                    Outstanding
                  </span>

                  <strong className="pb-summary-value">
                    {money(
                      cards.total_outstanding
                    )}
                  </strong>

                  <small>
                    Pending collection
                  </small>
                </div>
              </div>
            </Col>
          </Row>

          {/* =================================================
              SECONDARY CARDS
          ================================================= */}

          <Row className="g-3 mb-3">
            <Col
              xl={3}
              md={6}
            >
              <div className="pb-mini-card">
                <span>
                  Bill Amount
                </span>

                <strong>
                  {money(
                    cards.total_bill_amount
                  )}
                </strong>
              </div>
            </Col>

            <Col
              xl={3}
              md={6}
            >
              <div className="pb-mini-card">
                <span>
                  Invoice Amount
                </span>

                <strong>
                  {money(
                    cards.total_invoice_amount
                  )}
                </strong>
              </div>
            </Col>

            <Col
              xl={2}
              md={6}
            >
              <div className="pb-mini-card">
                <span>
                  Invoiced
                </span>

                <strong>
                  {cards.invoiced_bills ||
                    0}
                </strong>
              </div>
            </Col>

            <Col
              xl={2}
              md={6}
            >
              <div className="pb-mini-card balance">
                <span>
                  Partial
                </span>

                <strong>
                  {cards.partially_paid_bills ||
                    0}
                </strong>
              </div>
            </Col>

            <Col
              xl={2}
              md={6}
            >
              <div className="pb-mini-card received">
                <span>
                  Cleared
                </span>

                <strong>
                  {cards.cleared_bills ||
                    0}
                </strong>
              </div>
            </Col>
          </Row>

          {/* =================================================
              TABLES
          ================================================= */}

          <Row className="g-3">
            {/* ===============================================
                OUTSTANDING
            =============================================== */}

            <Col xl={7}>
              <Card className="pb-table-card h-100">
                <div className="pb-table-title">
                  <div>
                    <h5>
                      Panchayath
                      Outstanding
                    </h5>

                    <span>
                      Highest pending
                      collections
                    </span>
                  </div>
                </div>

                <div className="pb-table-scroll">
                  <Table
                    hover
                    className="pb-table align-middle"
                  >
                    <thead>
                      <tr>
                        <th>
                          Panchayath
                        </th>

                        <th>
                          Bills
                        </th>

                        <th>
                          Amount
                        </th>

                        <th>
                          Received
                        </th>

                        <th>
                          Balance
                        </th>
                      </tr>
                    </thead>

                    <tbody>
                      {panchayath_outstanding.length ? (
                        panchayath_outstanding.map(
                          (item) => (
                            <tr
                              key={
                                item.pan_id
                              }
                            >
                              <td>
                                <div className="pb-pan-name">
                                  {
                                    item.pan_name
                                  }
                                </div>

                                <small className="pb-pan-id">
                                  ID #
                                  {
                                    item.pan_id
                                  }
                                </small>
                              </td>

                              <td>
                                {
                                  item.total_bills
                                }
                              </td>

                              <td className="pb-money">
                                {money(
                                  item.total_amount
                                )}
                              </td>

                              <td className="pb-money pb-received">
                                {money(
                                  item.received_amount
                                )}
                              </td>

                              <td className="pb-money pb-balance">
                                {money(
                                  item.balance_amount
                                )}
                              </td>
                            </tr>
                          )
                        )
                      ) : (
                        <tr>
                          <td
                            colSpan={5}
                            className="pb-empty-cell"
                          >
                            No outstanding
                            amounts.
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </Table>
                </div>
              </Card>
            </Col>

            {/* ===============================================
                CATEGORY SUMMARY
            =============================================== */}

            <Col xl={5}>
              <Card className="pb-table-card h-100">
                <div className="pb-table-title">
                  <div>
                    <h5>
                      Category Summary
                    </h5>

                    <span>
                      Billing by
                      category
                    </span>
                  </div>
                </div>

                <div className="pb-table-scroll">
                  <Table
                    hover
                    className="pb-table align-middle"
                  >
                    <thead>
                      <tr>
                        <th>
                          Category
                        </th>

                        <th>
                          Bills
                        </th>

                        <th>
                          Received
                        </th>

                        <th>
                          Balance
                        </th>
                      </tr>
                    </thead>

                    <tbody>
                      {category_summary.length ? (
                        category_summary.map(
                          (item) => (
                            <tr
                              key={
                                item.bill_category
                              }
                            >
                              <td>
                                {labelize(
                                  item.bill_category
                                )}
                              </td>

                              <td>
                                {
                                  item.total_bills
                                }
                              </td>

                              <td className="pb-money pb-received">
                                {money(
                                  item.received_amount
                                )}
                              </td>

                              <td className="pb-money pb-balance">
                                {money(
                                  item.balance_amount
                                )}
                              </td>
                            </tr>
                          )
                        )
                      ) : (
                        <tr>
                          <td
                            colSpan={4}
                            className="pb-empty-cell"
                          >
                            No billing data.
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </Table>
                </div>
              </Card>
            </Col>
          </Row>

          {/* =================================================
              RECENT PAYMENTS
          ================================================= */}

          <Card className="pb-table-card mt-3">
            <div className="pb-table-title">
              <div>
                <h5>
                  Recent Payments
                </h5>

                <span>
                  Latest Panchayath
                  receipts
                </span>
              </div>
            </div>

            <div className="pb-table-scroll">
              <Table
                hover
                className="pb-table align-middle"
              >
                <thead>
                  <tr>
                    <th>
                      Receipt
                    </th>

                    <th>
                      Date
                    </th>

                    <th>
                      Panchayath
                    </th>

                    <th>
                      Invoice
                    </th>

                    <th>
                      Category
                    </th>

                    <th>
                      Mode
                    </th>

                    <th>
                      Amount
                    </th>
                  </tr>
                </thead>

                <tbody>
                  {recent_receipts.length ? (
                    recent_receipts.map(
                      (item) => (
                        <tr
                          key={
                            item.receipt_id
                          }
                        >
                          <td>
                            {item.receipt_number ||
                              `#${item.receipt_id}`}
                          </td>

                          <td>
                            {
                              item.receipt_date
                            }
                          </td>

                          <td>
                            {
                              item.pan_name
                            }
                          </td>

                          <td>
                            {item.invoice_number ||
                              "-"}
                          </td>

                          <td>
                            {labelize(
                              item.bill_category
                            )}
                          </td>

                          <td>
                            <span className="pb-payment-mode">
                              {labelize(
                                item.payment_mode
                              )}
                            </span>
                          </td>

                          <td className="pb-money pb-received">
                            {money(
                              item.received_amount
                            )}
                          </td>
                        </tr>
                      )
                    )
                  ) : (
                    <tr>
                      <td
                        colSpan={7}
                        className="pb-empty-cell"
                      >
                        No payments
                        found.
                      </td>
                    </tr>
                  )}
                </tbody>
              </Table>
            </div>
          </Card>

          {/* =================================================
              MONTHLY SUMMARY
          ================================================= */}

          <Card className="pb-table-card mt-3">
            <div className="pb-table-title">
              <div>
                <h5>
                  Monthly Billing vs
                  Collection
                </h5>

                <span>
                  Latest monthly
                  account movement
                </span>
              </div>
            </div>

            <div className="pb-table-scroll">
              <Table
                hover
                className="pb-table align-middle"
              >
                <thead>
                  <tr>
                    <th>
                      Month
                    </th>

                    <th>
                      Billing Amount
                    </th>

                    <th>
                      Received Amount
                    </th>

                    <th>
                      Difference
                    </th>
                  </tr>
                </thead>

                <tbody>
                  {monthly_chart.length ? (
                    monthly_chart.map(
                      (item) => (
                        <tr
                          key={
                            item.month_key
                          }
                        >
                          <td>
                            {
                              item.month_name
                            }
                          </td>

                          <td className="pb-money">
                            {money(
                              item.billing_amount
                            )}
                          </td>

                          <td className="pb-money pb-received">
                            {money(
                              item.received_amount
                            )}
                          </td>

                          <td className="pb-money pb-balance">
                            {money(
                              Number(
                                item.billing_amount ||
                                  0
                              ) -
                                Number(
                                  item.received_amount ||
                                    0
                                )
                            )}
                          </td>
                        </tr>
                      )
                    )
                  ) : (
                    <tr>
                      <td
                        colSpan={4}
                        className="pb-empty-cell"
                      >
                        No monthly data.
                      </td>
                    </tr>
                  )}
                </tbody>
              </Table>
            </div>
          </Card>
        </>
      )}
    </div>
  );
};

export default AccountDashboardPage;